import React, { CSSProperties, useMemo } from 'react'
import { NodeApi, TreeApi } from 'react-arborist'
import { Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Switch from 'packages/ui/Switch'

import { Device, TreeNode as TreeNodeType, Map } from '../../types'
import BaseNode from './BaseNode'
import { useAccessPointZonesContext } from '../../AccessPointZonesContext'

type TreeCommonNodeProps = {
  style: CSSProperties
  node: NodeApi<TreeNodeType>
  tree: TreeApi<TreeNodeType>
  onMapDisable: (map: Map) => void
  onDeviceDisable: (device: Device) => void
  onClick: (node: NodeApi<TreeNodeType>) => void
}

type SwitchActionProps = {
  node: NodeApi<TreeNodeType>
  onMapDisable: (map: Map) => void
  onDeviceDisable: (device: Device) => void
}

const SwitchAction = ({
  node,
  onMapDisable,
  onDeviceDisable
}: SwitchActionProps) => {
  const { apiActions } = useAccessPointZonesContext()

  const loading = useMemo(() => {
    if (node.data.data.type === 'map') {
      return node.data.data.devices.every((device) => apiActions.uuids.includes(device.uuid))
    } else if (node.data.data.type === 'device') {
      return apiActions.uuids.includes(node.data.data.uuid)
    }
  }, [node, apiActions.uuids])

  const checked = useMemo(() => {
    if (node.data.data.type === 'map') {
      return node.data.children.every(child => (child.data as Device).radio_enabled)
    }
    return (node.data.data as Device).radio_enabled
  }, [node])

  const handleSwitchMapChange = async () => {
    const deviceChildren = node.children?.filter(child => child.data.data.type === 'device') ?? []
    const allEnabled = deviceChildren.every(child => child.data.data.type === 'device' && child.data.data.radio_enabled)

    if (!allEnabled) {
      const devices = deviceChildren
        .map(child => child.data.data)
        .filter((device): device is Device & { type: 'device' } => device.type === 'device' && !device.radio_enabled)
      await apiActions.update({
        devices,
        enabled: !allEnabled,
        scope: 'floor',
        source: 'tree'
      })
    } else {
      onMapDisable(node.data.data as Map)
    }
  }

  const handleSwitchChange = async () => {
    if (node.data.data.type === 'map') {
      await handleSwitchMapChange()
    } else if (node.data.data.type === 'device') {
      if (node.data.data.radio_enabled && node.data.data.connected_stations > 0) {
        onDeviceDisable(node.data.data as Device)
      } else {
        await apiActions.update({
          devices: [node.data.data],
          enabled: !node.data.data.radio_enabled,
          scope: 'device',
          source: 'tree'
        })
      }
    }
  }
  return (
    <Stack onClick={(e: React.MouseEvent) => e.stopPropagation()} marginRight='4px'>
      {loading
        ? <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '5px' }} />
        : <Switch onChange={handleSwitchChange} checked={checked} />
      }
    </Stack>
  )
}

const Counter = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { tree } = useAccessPointZonesContext()
  const nbDevicesTotal = useMemo(() => {
    let nbDevices = 0
    const items = tree[0].data.type === 'site'
      ? tree[0].children
      : tree
    items.forEach(surface => {
      surface.children.forEach(child => {
        nbDevices += child.children.filter(({data}) => data.type === 'device').length
      })
    })
    return nbDevices
  }, [tree])

  const nbDevicesDisabled = useMemo(() => {
    let nbDevices = 0
    const items = tree[0].data.type === 'site'
      ? tree[0].children
      : tree
    items.forEach(surface => {
      surface.children.forEach(child => {
        const devices = child.children.filter(({ data }) => data.type === 'device' && !data.radio_enabled)
        nbDevices += devices.length
      })
    })
    return nbDevices
  }, [tree])

  return (
    <Stack direction={'row'} alignItems={'center'} sx={{ border: '1px solid #E5E7EB', borderRadius: '6px', padding: '4px 6px' }} gap={0.5}>
      {
        nbDevicesDisabled > 0 ? (
          <>
            <Typography sx={{ fontSize: '12px', fontWeight: 400, color: 'black', lineHeight: 1 }}>{nbDevicesDisabled}</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 400, color: theme.palette.grey[400], lineHeight: 1 }}>/</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 400, color: theme.palette.grey[400], lineHeight: 1 }}>{nbDevicesTotal}</Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: '11px', fontWeight: 400, color: theme.palette.grey[400], lineHeight: 1 }}>{t('access_point_zones.counter_zero')}</Typography>
        )
      }

      <i className="fa-light fa-signal-stream-slash" style={{ fontSize: '12px', color: theme.palette.grey[400] }} />
    </Stack>
  )
}



const Content = ({ node, onMapDisable, onDeviceDisable }: { node: NodeApi<TreeNodeType>, onMapDisable: (map: Map) => void, onDeviceDisable: (device: Device) => void }) => {
  const {mapUuid} = useAccessPointZonesContext()
  const theme = useTheme()
  const showSwitch = useMemo(() => {
    if (node.data.data.type === 'device') {
      return true
    } else if (node.data.data.type === 'map') {
      const children = node?.children?.filter(({ data }) => data.data.type !== 'empty') || []
      return children.length > 0
    }
    return false
  }, [node])
  return (
    <>
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 400,
          ...(node.data.data.type === 'empty' ? {color: '#9CA3AF', fontStyle: 'italic'} : {color: theme.palette.grey[800], }),
          ...(node.data.id === mapUuid && {fontWeight: 500}),
          lineHeight: 1
      }}>
        {node.data.name}
      </Typography>
      {
        node.level === 0 && (
          <Counter />
        )
      }
      {
        showSwitch && (
          <SwitchAction node={node} onMapDisable={onMapDisable} onDeviceDisable={onDeviceDisable} />
        )
      }
    </>
  )
}

const Node = ({ node, onClick, onMapDisable, onDeviceDisable }: TreeCommonNodeProps) => {
  const { onDeviceOver, deviceHovered } = useAccessPointZonesContext()
  const isLastChild = !node.nextSibling

  const isHovered = useMemo(() => {
    if (node.data.data.type !== 'device' || !deviceHovered) {
      return false
    }
    return node.data.id === deviceHovered.uuid
  }, [node.data.data.type, node.data.id, deviceHovered])

  const hidePrevBorder = useMemo(() => {
    // Pour chaque niveau, vérifier si le noeud à ce niveau est le dernier de sa fratrie
    // On ne traite pas le niveau 0 (racine) car il n'a pas de bordure
    const result: boolean[] = []
    let current: NodeApi<TreeNodeType> | null = node

    while (current?.parent && current.level > 0) {
      // Cacher la bordure si le noeud à ce niveau est le dernier de sa fratrie
      const isLast = !current.nextSibling
      result.unshift(isLast)
      current = current.parent
    }

    return result
  }, [node])

  const icon = useMemo(() => {
    if (node.data.data.type === 'site') {
      return 'fa-light fa-map-location-dot'
    } else if (node.data.data.type === 'surface_area') {
      return 'fa-light fa-building'
    } else if (node.data.data.type === 'map') {
      return 'fa-light fa-layer-group'
    }
  }, [node])

  return (
    <BaseNode
      isLastChild={isLastChild}
      hidePrevBorder={hidePrevBorder}
      node={node}
      icon={icon as string}
      onClick={() => onClick(node)}
      onMouseEnter={() => {
        if (node.data.data.type === 'device') {
          onDeviceOver(node.data.data as Device)
        }
      }}
      onMouseLeave={() => {
        onDeviceOver(null)
      }}
      hovered={isHovered}
      content={<Content node={node} onMapDisable={onMapDisable} onDeviceDisable={onDeviceDisable} />}
    />
  )
}

export default Node